import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

import { useInterval } from '~/components/AiChat/hooks/useInterval';
import { useClaim } from '~/components/ClaimContainer';
import type { ClaimAISummaryResponse } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

// Backend status type from API contract + FE-only states
type BackendSummaryStatus = ClaimAISummaryResponse['status'];
export type SummaryProcessingState = BackendSummaryStatus | 'loading' | 'timeout';

interface UseClaimAISummaryReturn {
  summary: ClaimAISummaryResponse | null;
  processingState: SummaryProcessingState;
  isPolling: boolean;
  isRefreshing: boolean;
  hasExistingSummary: boolean;
  triggerRefresh: () => Promise<void>;
}

const POLLING_INTERVAL_MS = 5000; // 5 sec
const POLLING_TIMEOUT_MS = 120000; // 120 sec (2 min)

export const useClaimAISummary = (): UseClaimAISummaryReturn => {
  const { claim } = useClaim({ claimRequired: true });
  const [summary, setSummary] = useState<ClaimAISummaryResponse | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  const pollingStartTimeRef = useRef<number | null>(null);

  const baseRoute = `/api/v1/claims/${claim.id}/summary`;

  const fetchSummary = useCallback(async (): Promise<ClaimAISummaryResponse | null> => {
    try {
      const { data } = await axios.get<ClaimAISummaryResponse>(baseRoute);
      setSummary(data);
      return data;
    } catch (err) {
      await reportAxiosError(err, false);
      return null;
    }
  }, [baseRoute]);

  // Polling callback - fetches summary and stops polling when complete or timed out
  const pollCallback = useCallback(async () => {
    if (pollingStartTimeRef.current && Date.now() - pollingStartTimeRef.current > POLLING_TIMEOUT_MS) {
      setIsPolling(false);
      setHasTimedOut(true);
      return;
    }

    const data = await fetchSummary();
    if (data && (data.status === 'success' || data.status === 'failed')) {
      setIsPolling(false);
    }
  }, [fetchSummary]);

  const { start: startInterval, stop: stopInterval } = useInterval(pollCallback, POLLING_INTERVAL_MS);

  // Start polling with timeout tracking
  const startPolling = useCallback(() => {
    pollingStartTimeRef.current = Date.now();
    setIsPolling(true);
    setHasTimedOut(false);
    startInterval();
  }, [startInterval]);

  // Stop polling and clean up
  const stopPolling = useCallback(() => {
    pollingStartTimeRef.current = null;
    setIsPolling(false);
    stopInterval();
  }, [stopInterval]);

  // Control polling based on isPolling state
  useEffect(() => {
    if (!isPolling) {
      stopInterval();
    }
  }, [isPolling, stopInterval]);

  const triggerRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const { data } = await axios.post(`${baseRoute}/refresh`);
      // Only start polling if the refresh was triggered successfully
      if (data.status === 'processing') {
        setSummary((prev: ClaimAISummaryResponse | null) => (prev ? { ...prev, status: 'processing' } : null));
        startPolling();
      } else if (data.status === 'failed') {
        // Refresh failed immediately - update summary to show failed state
        setSummary((prev: ClaimAISummaryResponse | null) =>
          prev ? { ...prev, status: 'failed' } : { status: 'failed' }
        );
      }
      // If status is 'success', no need to poll - summary is already up to date
    } catch (err) {
      // On error, update summary to failed state so user can retry
      setSummary((prev: ClaimAISummaryResponse | null) =>
        prev ? { ...prev, status: 'failed' } : { status: 'failed' }
      );
      await reportAxiosError(err);
    } finally {
      setIsRefreshing(false);
    }
  }, [baseRoute, startPolling]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setIsInitialLoading(true);
      const data = await fetchSummary();
      setIsInitialLoading(false);

      // A failed attempt on top of an existing summary: the card shows that summary, so
      // re-running generation on every mount only re-fires work the AI service just rejected.
      // The refresh button stays available for an explicit retry.
      if (data?.status === 'failed' && data.summary_text) {
        return;
      }

      if (!data || data.status === 'pending' || data.status === 'failed' || data.needs_refresh) {
        await triggerRefresh();
      } else if (data.status === 'processing') {
        startPolling();
      }
    };

    init();

    return () => {
      stopPolling();
    };
  }, [claim.id, fetchSummary, triggerRefresh, startPolling, stopPolling]);

  // Compute processing state
  const computeProcessingState = (): SummaryProcessingState => {
    if (hasTimedOut) return 'timeout';
    if (isInitialLoading) return 'loading';
    if (!summary) return 'loading';

    switch (summary.status) {
      case 'pending':
      case 'processing':
        return 'processing';
      case 'success':
        return 'success';
      case 'failed':
        return 'failed';
      default:
        return 'loading';
    }
  };

  return {
    summary,
    processingState: computeProcessingState(),
    isPolling,
    isRefreshing,
    hasExistingSummary: Boolean(summary?.summary_text),
    triggerRefresh,
  };
};
